/**
 * Style B — Borderless text with gold accent rule
 * No background box. Text only, heavy drop shadow for readability.
 * A thin gold horizontal rule separates name from role.
 */
import { useCurrentFrame, useVideoConfig, interpolate, Easing, AbsoluteFill } from "remotion";
import { loadFont } from "@remotion/google-fonts/Inter";
import { loadFont as loadDevanagari } from "@remotion/google-fonts/NotoSansDevanagari";

const { fontFamily } = loadFont("normal", { weights: ["400", "600", "700", "800"], subsets: ["latin"] });
const { fontFamily: devFont } = loadDevanagari("normal", { weights: ["400", "700"], subsets: ["devanagari"] });

const GOLD = "#D4A853";
const SHADOW = "0 2px 12px rgba(0,0,0,0.95), 0 0 30px rgba(0,0,0,0.9), 0 1px 3px rgba(0,0,0,1)";

const TextTag: React.FC<{
  name: string; nameHindi?: string; role: string; period?: string;
  frame: number; fps: number; durationInFrames: number;
  side: "left" | "right"; bottom?: number;
}> = ({ name, nameHindi, role, period, frame, fps, durationInFrames, side, bottom = 60 }) => {
  const fadeIn = interpolate(frame, [0, Math.round(0.4 * fps)], [0, 1], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  const slideX = interpolate(frame, [0, Math.round(0.35 * fps)], [side === "left" ? -20 : 20, 0], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  const fadeOut = interpolate(frame, [durationInFrames - Math.round(0.5 * fps), durationInFrames], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

  return (
    <div style={{
      position: "absolute", bottom, [side]: 28,
      opacity: fadeIn * fadeOut,
      transform: `translateX(${slideX}px)`,
      maxWidth: 360,
      textAlign: side === "left" ? "left" : "right",
    }}>
      {/* Gold rule above */}
      <div style={{ width: 40, height: 2, background: GOLD, marginBottom: 8, marginLeft: side === "right" ? "auto" : 0, boxShadow: "0 0 8px rgba(212,168,83,0.6)" }} />
      <div style={{
        color: "#fff", fontSize: 20, fontWeight: 800, fontFamily,
        lineHeight: 1.15, textShadow: SHADOW,
      }}>
        {name}
      </div>
      {nameHindi && (
        <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 12, fontWeight: 400, fontFamily: devFont, marginTop: 2, textShadow: SHADOW }}>
          {nameHindi}
        </div>
      )}
      <div style={{ color: GOLD, fontSize: 11, fontWeight: 700, fontFamily, marginTop: 5, textTransform: "uppercase", letterSpacing: "1.2px", textShadow: SHADOW }}>
        {role}
      </div>
      {period && (
        <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 10, fontFamily, marginTop: 2, textShadow: SHADOW }}>
          {period}
        </div>
      )}
    </div>
  );
};

const YearText: React.FC<{
  display: string; displayLatin?: string; context?: string;
  frame: number; fps: number; durationInFrames: number;
}> = ({ display, displayLatin, context, frame, fps, durationInFrames }) => {
  const fadeIn = interpolate(frame, [0, Math.round(0.4 * fps)], [0, 1], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
  });
  const fadeOut = interpolate(frame, [durationInFrames - Math.round(0.5 * fps), durationInFrames], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });
  const isDevanagari = /[ऀ-ॿ]/.test(display);

  return (
    <div style={{
      position: "absolute", top: 28, left: 28,
      opacity: fadeIn * fadeOut,
      maxWidth: 340,
    }}>
      <div style={{
        color: GOLD, fontSize: isDevanagari ? 22 : 30, fontWeight: 800,
        fontFamily: isDevanagari ? devFont : fontFamily,
        lineHeight: 1, textShadow: SHADOW,
      }}>
        {display}
      </div>
      {/* Gold rule below year */}
      <div style={{ width: 32, height: 2, background: GOLD, marginTop: 6, marginBottom: 4, boxShadow: "0 0 8px rgba(212,168,83,0.5)" }} />
      {displayLatin && (
        <div style={{ color: "rgba(212,168,83,0.7)", fontSize: 10, fontWeight: 600, fontFamily, letterSpacing: "0.5px", textShadow: SHADOW }}>
          {displayLatin}
        </div>
      )}
      {context && (
        <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, fontFamily, marginTop: 5, lineHeight: 1.4, textShadow: SHADOW }}>
          {context}
        </div>
      )}
    </div>
  );
};

export const StyleB_Preview: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      <TextTag
        name="Nara and Narayana" nameHindi="नर और नारायण"
        role="Sages performing penance at Badri Ashram" period="Mythological Era"
        frame={frame} fps={fps} durationInFrames={durationInFrames}
        side="left" bottom={60}
      />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
